import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, PawPrint, Home, Users, Send, CheckCircle, Loader2, Phone, Mail, MapPin, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MINISTRY_BANNER = 'https://static.prod-images.emergentagent.com/jobs/9bd4de2a-403c-4211-ba6a-7e7952c06d35/images/0f840a9da0fc7fdcf5f799cedc92d5d0049c007d763102e1d4304916e6d82131.png';

// Smallest Souls Animal Rescue gallery images
const rescueGalleryImages = [
  {
    url: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_d5b6a475-12ed-4057-ae9d-2a4f013301cd/artifacts/9wg3y0qi_IMG_6196%20%281%29.jpg',
    title: 'Adorable Rescue Puppy',
    description: 'Looking for a forever home',
    rotation: 0
  },
  {
    url: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_d5b6a475-12ed-4057-ae9d-2a4f013301cd/artifacts/95osg0jk_IMG_2618.webp',
    title: 'Smallest Souls Booth',
    description: 'Our adoption event booth',
    rotation: 90
  },
  {
    url: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_d5b6a475-12ed-4057-ae9d-2a4f013301cd/artifacts/mvgp885f_IMG_2613.webp',
    title: 'Pet Supplies',
    description: 'Donations and supplies for our rescue animals',
    rotation: 90
  },
  {
    url: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_d5b6a475-12ed-4057-ae9d-2a4f013301cd/artifacts/axetokuw_IMG_2617.webp',
    title: 'Community Event',
    description: 'Smallest Souls at a community adoption event',
    rotation: 90
  },
  {
    url: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_d5b6a475-12ed-4057-ae9d-2a4f013301cd/artifacts/ur548d1w_IMG_2619.webp',
    title: 'Volunteers in Action',
    description: 'Our amazing volunteers helping at events',
    rotation: 90
  },
  {
    url: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_d5b6a475-12ed-4057-ae9d-2a4f013301cd/artifacts/2ng1q2aj_IMG_2620.webp',
    title: 'Adoption Table',
    description: 'Adoption applications and donation info',
    rotation: 90
  }
];

const registrationTypes = [
  {
    id: 'volunteer',
    title: 'Volunteer',
    icon: Users,
    description: 'Help care for animals, assist at events, transport pets, or provide administrative support.',
    color: '#c07030'
  },
  {
    id: 'adopt',
    title: 'Adopt',
    icon: Heart,
    description: 'Give a forever home to a rescue animal in need of love and care.',
    color: '#8B4513'
  },
  {
    id: 'foster',
    title: 'Foster',
    icon: Home,
    description: 'Provide temporary care for animals until they find their forever homes.',
    color: '#1A1A1A'
  }
];

export default function AnimalRescuePage() {
  const [selectedType, setSelectedType] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: 'FL',
    zip: '',
    housing: '',
    hasYard: '',
    hasPets: '',
    petDetails: '',
    hasChildren: '',
    childrenAges: '',
    experience: '',
    availability: '',
    interests: '',
    whyInterested: '',
    references: '',
    agreeTerms: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.agreeTerms) {
      toast.error('Please agree to the terms to continue');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/animal-rescue/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: selectedType,
          ...formData
        })
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success('Registration submitted successfully!');
      } else {
        toast.error('Failed to submit. Please try again.');
      }
    } catch (error) {
      // Store locally if API not available
      const registrations = JSON.parse(localStorage.getItem('lfm_animal_registrations') || '[]');
      registrations.push({
        id: Date.now(),
        type: selectedType,
        ...formData,
        submittedAt: new Date().toISOString()
      });
      localStorage.setItem('lfm_animal_registrations', JSON.stringify(registrations));
      setSubmitted(true);
      toast.success('Registration submitted successfully!');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedType(null);
    setFormData({
      name: '', email: '', phone: '', address: '', city: '', state: 'FL', zip: '',
      housing: '', hasYard: '', hasPets: '', petDetails: '', hasChildren: '',
      childrenAges: '', experience: '', availability: '', interests: '',
      whyInterested: '', references: '', agreeTerms: false
    });
    setSubmitted(false);
  };

  return (
    <div className="min-h-screen bg-cream pt-20" data-testid="animal-rescue-page">
      {/* Banner */}
      <div className="w-full">
        <img src={MINISTRY_BANNER} alt="Living Fruit Ministry" className="w-full h-auto" />
      </div>

      {/* Hero Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-amber-50 to-cream">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 text-amber-800 text-sm font-medium mb-6">
              <PawPrint size={16} />
              Animal Care Ministry
            </div>
            
            <h1 className="font-serif text-4xl md:text-5xl mb-6" style={{ color: '#1A1A1A' }}>
              Animal Rescue Services
            </h1>
            
            <p className="text-lg md:text-xl mb-4" style={{ color: '#1E3A5F' }}>
              We are proudly partnering with
            </p>
            
            <div className="inline-block bg-white rounded-2xl px-8 py-4 shadow-lg border-2 border-amber-200 mb-4">
              <h2 className="font-serif text-2xl md:text-3xl font-bold" style={{ color: '#8B4513' }}>
                Smallest Souls Rescue
              </h2>
            </div>
            
            <div className="flex justify-center mb-8">
              <div className="inline-flex items-center gap-2 bg-emerald-100 px-4 py-2 rounded-full">
                <Heart size={16} className="text-emerald-600" />
                <span className="text-sm font-medium text-emerald-800">501(c)(3) Nonprofit Organization</span>
              </div>
            </div>
            
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#1E3A5F' }}>
              Together, we're committed to rescuing, rehabilitating, and rehoming animals in need. 
              Every soul matters, no matter how small.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Donation CTA Section */}
      <section className="py-12 px-6 bg-gradient-to-r from-amber-50 to-emerald-50">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-amber-200">
              <PawPrint size={48} className="mx-auto mb-4 text-amber-600" />
              <h2 className="font-serif text-2xl md:text-3xl mb-4" style={{ color: '#8B4513' }}>
                Support Our Rescue Mission
              </h2>
              <p className="text-charcoal/70 mb-6 max-w-xl mx-auto">
                Your donation helps us rescue, rehabilitate, and rehome animals in need. 
                Smallest Souls Animal Rescue is a 501(c)(3) nonprofit - all donations are tax-deductible.
              </p>
              <a 
                href="https://www.smallestsoulsanimalrescue.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 rounded-full font-semibold text-lg transition-colors"
                data-testid="animal-rescue-donate-link"
              >
                <Heart size={20} />
                <span>Donate at smallestsoulsanimalrescue.com</span>
                <ExternalLink size={18} />
              </a>
              <p className="text-sm text-charcoal/50 mt-4">
                Opens in a new tab
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="font-serif text-3xl mb-4" style={{ color: '#8B4513' }}>
              Our Rescue in Action
            </h2>
            <p style={{ color: '#1E3A5F' }}>
              See the impact of your support at our adoption events and rescue operations
            </p>
          </motion.div>

          {/* Main Gallery Image */}
          <div className="relative rounded-2xl overflow-hidden shadow-xl mb-4" style={{ height: '500px' }}>
            <div className="w-full h-full flex items-center justify-center bg-gray-100">
              <motion.img
                key={currentImageIndex}
                src={rescueGalleryImages[currentImageIndex]?.url}
                alt={rescueGalleryImages[currentImageIndex]?.title || 'Rescue photo'}
                className="max-w-full max-h-full object-contain"
                style={{ 
                  transform: `rotate(${rescueGalleryImages[currentImageIndex]?.rotation || 0}deg)`,
                  maxHeight: rescueGalleryImages[currentImageIndex]?.rotation ? '140%' : '100%',
                  maxWidth: rescueGalleryImages[currentImageIndex]?.rotation ? '140%' : '100%'
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              />
            </div>
            
            {/* Navigation Arrows */}
            <button
              onClick={() => setCurrentImageIndex((prev) => (prev - 1 + rescueGalleryImages.length) % rescueGalleryImages.length)}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/80 rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-lg"
            >
              <ChevronLeft size={28} />
            </button>
            <button
              onClick={() => setCurrentImageIndex((prev) => (prev + 1) % rescueGalleryImages.length)}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/80 rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-lg"
            >
              <ChevronRight size={28} />
            </button>
            
            {/* Image Caption */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
              <p className="text-white font-medium text-lg">{rescueGalleryImages[currentImageIndex]?.title}</p>
              <p className="text-white/70 text-sm">{rescueGalleryImages[currentImageIndex]?.description}</p>
            </div>
          </div>
          
          {/* Thumbnails */}
          <div className="flex gap-3 justify-center flex-wrap">
            {rescueGalleryImages.map((image, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`flex-shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden border-3 transition-all ${
                  index === currentImageIndex 
                    ? 'border-amber-500 scale-105 shadow-lg' 
                    : 'border-transparent opacity-60 hover:opacity-100'
                }`}
              >
                <img 
                  src={image.url} 
                  alt="" 
                  className="w-full h-full object-cover" 
                  style={{ transform: `rotate(${image.rotation || 0}deg)` }}
                />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* How You Can Help */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-3xl mb-4" style={{ color: '#1A1A1A' }}>
              How You Can Help
            </h2>
            <p style={{ color: '#1E3A5F' }}>
              Choose how you'd like to make a difference in an animal's life
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {registrationTypes.map((type, index) => {
              const Icon = type.icon;
              const isSelected = selectedType === type.id;
              
              return (
                <motion.button
                  key={type.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => {
                    setSelectedType(type.id);
                    setSubmitted(false);
                  }}
                  className={`p-8 rounded-2xl text-left transition-all hover:scale-[1.02] ${
                    isSelected 
                      ? 'bg-white shadow-xl ring-2 ring-amber-400' 
                      : 'bg-white/70 shadow-md hover:shadow-lg'
                  }`}
                  data-testid={`select-${type.id}`}
                >
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${type.color}15` }}
                  >
                    <Icon size={28} style={{ color: type.color }} />
                  </div>
                  <h3 className="font-serif text-xl mb-2" style={{ color: '#1A1A1A' }}>
                    {type.title}
                  </h3>
                  <p className="text-sm" style={{ color: '#1E3A5F' }}>
                    {type.description}
                  </p>
                  {isSelected && (
                    <div className="mt-4 flex items-center gap-2 text-amber-600 text-sm font-medium">
                      <CheckCircle size={16} />
                      Selected
                    </div>
                  )}
                </motion.button>
              );
            })}
          </div>

          {/* Registration Form */}
          {selectedType && !submitted && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gold/10"
            >
              <h3 className="font-serif text-2xl mb-2" style={{ color: '#1A1A1A' }}>
                {selectedType === 'volunteer' && 'Volunteer Registration'}
                {selectedType === 'adopt' && 'Adoption Application'}
                {selectedType === 'foster' && 'Foster Application'}
              </h3>
              <p className="text-sm mb-8" style={{ color: '#1E3A5F' }}>
                Please fill out the form below and we'll be in touch soon.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="animal-rescue-form">
                {/* Personal Information */}
                <div className="border-b border-gold/10 pb-6">
                  <h4 className="font-medium text-charcoal mb-4">Personal Information</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Full Name *</label>
                      <input
                        type="text"
                        required
                        maxLength={100}
                        pattern="[A-Za-z\s\-']{2,100}"
                        title="Please enter a valid name (letters, spaces, hyphens only)"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value.slice(0, 100)})}
                        className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50"
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Email *</label>
                      <input
                        type="email"
                        required
                        maxLength={254}
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value.slice(0, 254)})}
                        className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50"
                        data-testid="input-email"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Phone *</label>
                      <input
                        type="tel"
                        required
                        maxLength={20}
                        pattern="[\d\s\-\(\)\+]{7,20}"
                        title="Please enter a valid phone number"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value.replace(/[^\d\s\-\(\)\+]/g, '').slice(0, 20)})}
                        className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50"
                        data-testid="input-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">City *</label>
                      <input
                        type="text"
                        required
                        maxLength={100}
                        value={formData.city}
                        onChange={(e) => setFormData({...formData, city: e.target.value.slice(0, 100)})}
                        className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50"
                      />
                    </div>
                  </div>
                </div>

                {/* Housing Information (for Adopt/Foster) */}
                {(selectedType === 'adopt' || selectedType === 'foster') && (
                  <div className="border-b border-gold/10 pb-6">
                    <h4 className="font-medium text-charcoal mb-4">Housing Information</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Type of Housing *</label>
                        <select
                          required
                          value={formData.housing}
                          onChange={(e) => setFormData({...formData, housing: e.target.value})}
                          className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50"
                        >
                          <option value="">Select...</option>
                          <option value="house">House</option>
                          <option value="apartment">Apartment</option>
                          <option value="condo">Condo</option>
                          <option value="townhouse">Townhouse</option>
                          <option value="mobile">Mobile Home</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Do you have a yard? *</label>
                        <select
                          required
                          value={formData.hasYard}
                          onChange={(e) => setFormData({...formData, hasYard: e.target.value})}
                          className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50"
                        >
                          <option value="">Select...</option>
                          <option value="yes-fenced">Yes, fenced</option>
                          <option value="yes-unfenced">Yes, unfenced</option>
                          <option value="no">No</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Household Information */}
                {(selectedType === 'adopt' || selectedType === 'foster') && (
                  <div className="border-b border-gold/10 pb-6">
                    <h4 className="font-medium text-charcoal mb-4">Household Information</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Do you have other pets? *</label>
                        <select
                          required
                          value={formData.hasPets}
                          onChange={(e) => setFormData({...formData, hasPets: e.target.value})}
                          className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50"
                        >
                          <option value="">Select...</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </div>
                      {formData.hasPets === 'yes' && (
                        <div>
                          <label className="block text-sm font-medium mb-1">Please describe your pets</label>
                          <input
                            type="text"
                            value={formData.petDetails}
                            onChange={(e) => setFormData({...formData, petDetails: e.target.value})}
                            placeholder="Type, breed, age..."
                            className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50"
                          />
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium mb-1">Do you have children? *</label>
                        <select
                          required
                          value={formData.hasChildren}
                          onChange={(e) => setFormData({...formData, hasChildren: e.target.value})}
                          className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50"
                        >
                          <option value="">Select...</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </div>
                      {formData.hasChildren === 'yes' && (
                        <div>
                          <label className="block text-sm font-medium mb-1">Ages of children</label>
                          <input
                            type="text"
                            value={formData.childrenAges}
                            onChange={(e) => setFormData({...formData, childrenAges: e.target.value})}
                            placeholder="e.g., 5, 8, 12"
                            className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Experience & Availability */}
                <div className="border-b border-gold/10 pb-6">
                  <h4 className="font-medium text-charcoal mb-4">
                    {selectedType === 'volunteer' ? 'Volunteer Information' : 'Experience & Interests'}
                  </h4>
                  
                  {selectedType === 'volunteer' && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-1">Areas of Interest *</label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {['Animal Care', 'Event Support', 'Transportation', 'Fundraising', 'Administrative', 'Social Media'].map(interest => (
                          <label key={interest} className="flex items-center gap-2 p-3 bg-cream rounded-lg cursor-pointer hover:bg-gold/10 transition-colors">
                            <input
                              type="checkbox"
                              checked={formData.interests.includes(interest)}
                              onChange={(e) => {
                                const current = formData.interests ? formData.interests.split(', ') : [];
                                if (e.target.checked) {
                                  setFormData({...formData, interests: [...current, interest].join(', ')});
                                } else {
                                  setFormData({...formData, interests: current.filter(i => i !== interest).join(', ')});
                                }
                              }}
                              className="rounded text-amber-600"
                            />
                            <span className="text-sm">{interest}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {selectedType === 'volunteer' ? 'Availability *' : 'Previous pet experience *'}
                      </label>
                      <textarea
                        required
                        rows={3}
                        maxLength={1000}
                        value={selectedType === 'volunteer' ? formData.availability : formData.experience}
                        onChange={(e) => setFormData({
                          ...formData, 
                          [selectedType === 'volunteer' ? 'availability' : 'experience']: e.target.value.slice(0, 1000)
                        })}
                        placeholder={selectedType === 'volunteer' 
                          ? 'When are you available to volunteer?' 
                          : 'Describe your experience with pets...'}
                        className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50 resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Why are you interested in {selectedType === 'volunteer' ? 'volunteering' : selectedType === 'adopt' ? 'adopting' : 'fostering'}? *
                      </label>
                      <textarea
                        required
                        rows={3}
                        maxLength={1000}
                        value={formData.whyInterested}
                        onChange={(e) => setFormData({...formData, whyInterested: e.target.value.slice(0, 1000)})}
                        className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50 resize-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Agreement */}
                <div className="bg-amber-50 rounded-xl p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.agreeTerms}
                      onChange={(e) => setFormData({...formData, agreeTerms: e.target.checked})}
                      className="mt-1 rounded text-amber-600"
                      data-testid="agree-terms"
                    />
                    <span className="text-sm" style={{ color: '#1E3A5F' }}>
                      I understand that submitting this application does not guarantee approval. 
                      I agree to provide accurate information and consent to a background check if required. 
                      I understand that Living Fruit Ministry and Smallest Souls Rescue reserve the right 
                      to deny any application.
                    </span>
                  </label>
                </div>

                {/* Submit */}
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <button
                    type="button"
                    onClick={() => setSelectedType(null)}
                    className="px-6 py-3 rounded-full text-sm font-medium"
                    style={{ color: '#1E3A5F' }}
                  >
                    ← Back
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-semibold text-white transition-all hover:scale-105 disabled:opacity-50"
                    style={{ backgroundColor: '#c07030' }}
                    data-testid="submit-registration"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        Submit Application
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          )}

          {/* Success Message */}
          {submitted && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-3xl p-12 shadow-xl text-center"
            >
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#c07030' }}>
                <CheckCircle className="text-white" size={40} />
              </div>
              <h3 className="font-serif text-2xl mb-4" style={{ color: '#1A1A1A' }}>
                Thank You for Your Application!
              </h3>
              <p className="mb-6" style={{ color: '#1E3A5F' }}>
                We've received your {selectedType} application. Our team will review it and contact you soon.
                Together with Smallest Souls Rescue, we're making a difference one animal at a time.
              </p>
              <button
                onClick={resetForm}
                className="px-6 py-3 rounded-full font-medium transition-colors"
                style={{ backgroundColor: '#f7ede0', color: '#c07030' }}
              >
                Submit Another Application
              </button>
            </motion.div>
          )}
        </div>
      </section>

      {/* About the Partnership */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-3xl mb-4" style={{ color: '#1A1A1A' }}>
              About Our Partnership
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-cream rounded-2xl p-8">
              <PawPrint size={32} className="mb-4" style={{ color: '#c07030' }} />
              <h3 className="font-serif text-xl mb-3" style={{ color: '#1A1A1A' }}>
                Smallest Souls Rescue
              </h3>
              <p className="text-sm" style={{ color: '#1E3A5F' }}>
                Smallest Souls Rescue is dedicated to saving the lives of animals in need, 
                focusing on those often overlooked. Through rescue, rehabilitation, and rehoming, 
                they give every animal a chance at a loving life.
              </p>
            </div>
            
            <div className="bg-cream rounded-2xl p-8">
              <Heart size={32} className="mb-4" style={{ color: '#c07030' }} />
              <h3 className="font-serif text-xl mb-3" style={{ color: '#1A1A1A' }}>
                Living Fruit Ministry
              </h3>
              <p className="text-sm" style={{ color: '#1E3A5F' }}>
                As part of our commitment to serving the community, our Animal Care Ministry 
                partners with local rescue organizations to help animals find loving homes 
                while teaching compassion and responsibility.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-6" style={{ backgroundColor: '#1A1A1A' }}>
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="font-serif text-3xl mb-4">Questions?</h2>
          <p className="mb-8 text-white/80">
            Contact our Animal Care Ministry Director for more information
          </p>
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <a href="mailto:smallestsouls@gmail.com" className="flex items-center gap-2 hover:text-gold transition-colors">
              <Mail size={20} className="text-gold" />
              <span>smallestsouls@gmail.com</span>
            </a>
            <a href="tel:954-298-1951" className="flex items-center gap-2 hover:text-gold transition-colors">
              <Phone size={20} className="text-gold" />
              <span>(954) 298-1951</span>
            </a>
          </div>
          <a 
            href="https://www.smallestsoulsanimalrescue.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-full font-semibold transition-colors"
          >
            <PawPrint size={20} />
            Visit smallestsoulsanimalrescue.com
            <ExternalLink size={18} />
          </a>
          <p className="mt-6 text-sm text-white/60">
            Ministry Director: Isela Merino
          </p>
        </div>
      </section>
    </div>
  );
}
